/**
 * Excel import helper for Customers and Vendors.
 * Generates a downloadable template and parses uploaded rows into clean
 * party records, then creates linked Chart-of-Accounts entries.
 */

import * as XLSX from 'xlsx';
import {
  collection, addDoc, updateDoc, doc, getDocs, query, where
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createLinkedAccount, ensureParentAccountExists } from './coaInitializer';

const yesNo = (v) => {
  if (v === true) return true;
  if (v === false) return false;
  if (v == null) return false;
  return String(v).trim().toLowerCase().startsWith('y');
};
const num = (v) => {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(/[, ]/g, ''));
  return isNaN(n) ? 0 : n;
};
// Like num(), but keeps the "blank = not set" distinction: empty → null (fall
// back to the standard rate on sale lines), a number (incl. 0) → explicit value.
const numOrNull = (v) => {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(String(v).replace(/[, ]/g, ''));
  return isNaN(n) ? null : n;
};
// Normalize a Customer Class cell to Retail / Wholesale / Dealer (case-insensitive),
// else null (no class → Retail behavior on sale documents).
const normClass = (v) => {
  const s = String(v || '').trim().toLowerCase();
  if (s === 'retail') return 'Retail';
  if (s === 'wholesale') return 'Wholesale';
  if (s === 'dealer') return 'Dealer';
  return null;
};
const str = (v) => (v == null ? '' : String(v).trim());

// ─── Customer Template ──────────────────────────────────────────────────────
export const downloadCustomerTemplate = () => {
  const sample = [
    {
      'Company Name': 'ABC Trading Co.',
      'Contact Person': 'Mr. Ahmed Khan',
      'Email': 'ahmed@abc.com',
      'Phone': '+92 300 1234567',
      'NTN': '1234567-8',
      'STRN': '03-12-3456-789-99',
      'Address': '123 Main Street',
      'Region': 'Sindh',
      'Area': 'Karachi',
      'Postal Code': '74000',
      'Country': 'Pakistan',
      'Credit Limit': 500000,
      'Credit Days': 30,
      'Discount %': 5,
      'Sales Tax %': 17,
      'Customer Class': 'Retail',
      'Opening Balance': 0,
      'Active (Yes/No)': 'Yes',
      'Notes': ''
    },
    {
      'Company Name': 'XYZ Distributors',
      'Contact Person': 'Ms. Sara Ali',
      'Email': 'sara@xyz.com',
      'Phone': '+92 321 9876543',
      'NTN': '9876543-2',
      'STRN': '',
      'Address': '45 Mall Road',
      'Region': 'Punjab',
      'Area': 'Lahore',
      'Postal Code': '54000',
      'Country': 'Pakistan',
      'Credit Limit': 750000,
      'Credit Days': 45,
      'Discount %': '',
      'Sales Tax %': '',
      'Customer Class': 'Wholesale',
      'Opening Balance': 25000,
      'Active (Yes/No)': 'Yes',
      'Notes': 'Long-term wholesale customer'
    }
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(sample);
  ws['!cols'] = [
    { wch: 28 }, { wch: 22 }, { wch: 26 }, { wch: 18 }, { wch: 14 }, { wch: 22 },
    { wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
    { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 28 }
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Customers');

  const instructions = [
    { Instructions: '=== CUSTOMER IMPORT TEMPLATE ===' }, { Instructions: '' },
    { Instructions: 'REQUIRED: Company Name' },
    { Instructions: 'OPTIONAL: all other columns' }, { Instructions: '' },
    { Instructions: 'BEHAVIOR:' },
    { Instructions: '• Each row creates ONE customer.' },
    { Instructions: '• If a Region/Area name is provided and does not exist yet, it is auto-created.' },
    { Instructions: '• A linked Chart-of-Accounts entry (under "Accounts Receivable") is created for every customer.' },
    { Instructions: '• Opening Balance, if > 0, is recorded against the linked account as a Draft Opening Balance entry — go to Master → Opening Balances to post it.' },
    { Instructions: '• Discount % and Sales Tax % are OPTIONAL per-customer defaults used on Sale Invoices. Leave BLANK to use the standard rate; 0 means zero (e.g. tax-exempt).' },
    { Instructions: '• Customer Class (OPTIONAL): Retail, Wholesale or Dealer — auto-picks that item price tier on sale documents. Leave blank for Retail. (Applies only when the Customer Class Pricing module is enabled.)' },
    { Instructions: '• Active = Yes/No (default Yes).' }, { Instructions: '' },
    { Instructions: 'TIPS:' },
    { Instructions: '• Delete the sample rows before importing your real data.' },
    { Instructions: '• Existing customers (matched by Company Name, case-insensitive) are skipped — not overwritten.' }
  ];
  const wsI = XLSX.utils.json_to_sheet(instructions);
  wsI['!cols'] = [{ wch: 90 }];
  XLSX.utils.book_append_sheet(wb, wsI, 'Instructions');

  XLSX.writeFile(wb, 'Customer_Import_Template.xlsx');
};

// ─── Vendor Template ────────────────────────────────────────────────────────
export const downloadVendorTemplate = () => {
  const sample = [
    {
      'Vendor Name': 'Pak Steel Mills',
      'Vendor Type': 'Supplier',
      'Contact Person': 'Mr. Imran Yousuf',
      'Email': 'imran@paksteel.com',
      'Phone': '+92 21 1234567',
      'Mobile': '+92 300 1234567',
      'Address': 'Plot 12, S.I.T.E.',
      'City': 'Karachi',
      'Country': 'Pakistan',
      'NTN': '1234567-8',
      'GST/STRN': '03-12-3456-789-99',
      'Payment Terms (Days)': 30,
      'Bank Name': 'HBL',
      'Bank Account No': '12345678901234',
      'Bank Branch': 'Main Branch Karachi',
      'WHT Applicable (Yes/No)': 'Yes',
      'WHT Rate (%)': 4.5,
      'Opening Balance': 0,
      'Active (Yes/No)': 'Yes',
      'Notes': ''
    },
    {
      'Vendor Name': 'Office Solutions Pvt Ltd',
      'Vendor Type': 'Service Provider',
      'Contact Person': 'Ms. Hina Riaz',
      'Email': 'hina@officesolutions.com',
      'Phone': '+92 42 9876543',
      'Mobile': '+92 333 9876543',
      'Address': '88 Mall Road',
      'City': 'Lahore',
      'Country': 'Pakistan',
      'NTN': '9876543-2',
      'GST/STRN': '',
      'Payment Terms (Days)': 60,
      'Bank Name': 'MCB',
      'Bank Account No': '99887766554433',
      'Bank Branch': 'Mall Road Lahore',
      'WHT Applicable (Yes/No)': 'No',
      'WHT Rate (%)': 0,
      'Opening Balance': 50000,
      'Active (Yes/No)': 'Yes',
      'Notes': 'Office maintenance contractor'
    }
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(sample);
  ws['!cols'] = [
    { wch: 30 }, { wch: 16 }, { wch: 22 }, { wch: 26 }, { wch: 18 }, { wch: 18 },
    { wch: 32 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 22 }, { wch: 16 },
    { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 12 }, { wch: 14 },
    { wch: 14 }, { wch: 28 }
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Vendors');

  const instructions = [
    { Instructions: '=== VENDOR IMPORT TEMPLATE ===' }, { Instructions: '' },
    { Instructions: 'REQUIRED: Vendor Name' },
    { Instructions: 'OPTIONAL: all other columns' }, { Instructions: '' },
    { Instructions: 'BEHAVIOR:' },
    { Instructions: '• Each row creates ONE vendor with an auto-generated VEND-XXXX code.' },
    { Instructions: '• A linked Chart-of-Accounts entry (under "Accounts Payable") is created for every vendor.' },
    { Instructions: '• Opening Balance, if > 0, is recorded against the linked account as a Draft entry.' },
    { Instructions: '• WHT Applicable = Yes/No, WHT Rate is a percent (e.g. 4.5).' },
    { Instructions: '• Active = Yes/No (default Yes).' },
    { Instructions: '• Vendor Type: Supplier, Contractor, Service Provider, etc.' }, { Instructions: '' },
    { Instructions: 'TIPS:' },
    { Instructions: '• Delete sample rows before importing real data.' },
    { Instructions: '• Existing vendors (matched by Vendor Name, case-insensitive) are skipped — not overwritten.' }
  ];
  const wsI = XLSX.utils.json_to_sheet(instructions);
  wsI['!cols'] = [{ wch: 90 }];
  XLSX.utils.book_append_sheet(wb, wsI, 'Instructions');

  XLSX.writeFile(wb, 'Vendor_Import_Template.xlsx');
};

// ─── Region/Area resolver ───────────────────────────────────────────────────
const ensureRegionArea = async (companyId, regionName, areaName) => {
  let regionId = '', areaId = '';
  if (!regionName && !areaName) return { regionId, areaId };

  const regCol = collection(db, 'companies', companyId, 'regions');
  if (regionName) {
    const regSnap = await getDocs(regCol);
    const found = regSnap.docs.find(d => (d.data().regionName || '').toLowerCase() === regionName.toLowerCase());
    if (found) regionId = found.id;
    else {
      const ref = await addDoc(regCol, { regionName, isActive: true, createdAt: new Date().toISOString() });
      regionId = ref.id;
    }
  }
  if (areaName) {
    const areaCol = collection(db, 'companies', companyId, 'areas');
    const areaSnap = await getDocs(areaCol);
    const found = areaSnap.docs.find(d => {
      const data = d.data();
      return (data.areaName || '').toLowerCase() === areaName.toLowerCase()
        && (!regionId || data.regionId === regionId);
    });
    if (found) areaId = found.id;
    else {
      const ref = await addDoc(areaCol, { areaName, regionId, isActive: true, createdAt: new Date().toISOString() });
      areaId = ref.id;
    }
  }
  return { regionId, areaId };
};

// ─── Customer Import ────────────────────────────────────────────────────────
export const importCustomersFromRows = async (companyId, rows, userId, onProgress) => {
  const results = { success: 0, skipped: 0, failed: 0, errors: [] };

  // Pre-fetch existing customers for de-dup
  const existingSnap = await getDocs(collection(db, 'companies', companyId, 'customers'));
  const existingByName = new Map();
  existingSnap.docs.forEach(d => {
    const name = (d.data().companyName || '').toLowerCase().trim();
    if (name) existingByName.set(name, d.id);
  });

  // Parent account check (auto-repairs COA if missing)
  const parentCheck = await ensureParentAccountExists(companyId, 'customer', userId);
  if (!parentCheck.success) {
    results.failed = rows.length;
    results.errors.push(`Cannot create linked accounts: ${parentCheck.message}`);
    return results;
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (onProgress) onProgress({ current: i + 1, total: rows.length });
    const companyName = str(row['Company Name'] || row['Customer Name']);
    if (!companyName) {
      results.failed++;
      results.errors.push(`Row ${i + 2}: Company Name is required.`);
      continue;
    }
    if (existingByName.has(companyName.toLowerCase())) {
      results.skipped++;
      continue;
    }

    try {
      const regionName = str(row['Region']);
      const areaName = str(row['Area']);
      const { regionId, areaId } = await ensureRegionArea(companyId, regionName, areaName);

      const data = {
        companyName,
        contactPerson: str(row['Contact Person']),
        email: str(row['Email']),
        phone: str(row['Phone']),
        ntn: str(row['NTN']),
        strn: str(row['STRN']),
        address: str(row['Address']),
        regionId,
        areaId,
        postalCode: str(row['Postal Code']),
        country: str(row['Country']) || 'Pakistan',
        creditLimit: num(row['Credit Limit']),
        creditDays: num(row['Credit Days']) || 30,
        // Per-customer sale defaults (optional). Blank → null (use standard rate).
        defaultDiscountPercent: numOrNull(row['Discount %']),
        defaultTaxRate: numOrNull(row['Sales Tax %']),
        customerClass: normClass(row['Customer Class']),
        openingBalance: num(row['Opening Balance']),
        isActive: row['Active (Yes/No)'] == null ? true : yesNo(row['Active (Yes/No)']),
        notes: str(row['Notes']),
        createdAt: new Date().toISOString(),
        createdBy: userId,
        updatedAt: new Date().toISOString()
      };

      const custCol = collection(db, 'companies', companyId, 'customers');
      const ref = await addDoc(custCol, data);
      existingByName.set(companyName.toLowerCase(), ref.id);

      // Linked account
      try {
        const acc = await createLinkedAccount(companyId, 'customer', ref.id, companyName, userId);
        if (acc.success) {
          await updateDoc(doc(custCol, ref.id), {
            linkedAccountId: acc.accountId,
            linkedAccountCode: acc.accountCode
          });
        } else {
          results.errors.push(`Row ${i + 2} (${companyName}): linked account not created — ${acc.message}`);
        }
      } catch (accErr) {
        results.errors.push(`Row ${i + 2} (${companyName}): linked account error — ${accErr.message}`);
      }

      results.success++;
    } catch (err) {
      results.failed++;
      results.errors.push(`Row ${i + 2} (${companyName}): ${err.message}`);
    }
  }
  return results;
};

// ─── Vendor Import ──────────────────────────────────────────────────────────
const generateVendorCode = (existingCount, batchOffset) =>
  `VEND-${String(existingCount + batchOffset + 1).padStart(4, '0')}`;

export const importVendorsFromRows = async (companyId, rows, userId, onProgress) => {
  const results = { success: 0, skipped: 0, failed: 0, errors: [] };

  const existingSnap = await getDocs(collection(db, 'companies', companyId, 'vendors'));
  const existingByName = new Map();
  existingSnap.docs.forEach(d => {
    const name = (d.data().vendorName || '').toLowerCase().trim();
    if (name) existingByName.set(name, d.id);
  });
  const baseCount = existingSnap.size;

  const parentCheck = await ensureParentAccountExists(companyId, 'vendor', userId);
  if (!parentCheck.success) {
    results.failed = rows.length;
    results.errors.push(`Cannot create linked accounts: ${parentCheck.message}`);
    return results;
  }

  let added = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (onProgress) onProgress({ current: i + 1, total: rows.length });
    const vendorName = str(row['Vendor Name']);
    if (!vendorName) {
      results.failed++;
      results.errors.push(`Row ${i + 2}: Vendor Name is required.`);
      continue;
    }
    if (existingByName.has(vendorName.toLowerCase())) {
      results.skipped++;
      continue;
    }

    try {
      const data = {
        vendorName,
        vendorCode: generateVendorCode(baseCount, added),
        vendorType: str(row['Vendor Type']) || 'Supplier',
        contactPerson: str(row['Contact Person']),
        email: str(row['Email']),
        phone: str(row['Phone']),
        mobile: str(row['Mobile']),
        address: str(row['Address']),
        city: str(row['City']),
        country: str(row['Country']) || 'Pakistan',
        ntn: str(row['NTN']),
        gstNo: str(row['GST/STRN']),
        paymentTerms: num(row['Payment Terms (Days)']) || 30,
        bankName: str(row['Bank Name']),
        bankAccountNo: str(row['Bank Account No']),
        bankBranch: str(row['Bank Branch']),
        whtApplicable: yesNo(row['WHT Applicable (Yes/No)']),
        whtRate: num(row['WHT Rate (%)']),
        openingBalance: num(row['Opening Balance']),
        isActive: row['Active (Yes/No)'] == null ? true : yesNo(row['Active (Yes/No)']),
        notes: str(row['Notes']),
        createdAt: new Date().toISOString(),
        createdBy: userId,
        updatedAt: new Date().toISOString()
      };

      const vCol = collection(db, 'companies', companyId, 'vendors');
      const ref = await addDoc(vCol, data);
      existingByName.set(vendorName.toLowerCase(), ref.id);
      added++;

      try {
        const acc = await createLinkedAccount(companyId, 'vendor', ref.id, vendorName, userId);
        if (acc.success) {
          await updateDoc(doc(vCol, ref.id), {
            linkedAccountId: acc.accountId,
            linkedAccountCode: acc.accountCode
          });
        } else {
          results.errors.push(`Row ${i + 2} (${vendorName}): linked account not created — ${acc.message}`);
        }
      } catch (accErr) {
        results.errors.push(`Row ${i + 2} (${vendorName}): linked account error — ${accErr.message}`);
      }

      results.success++;
    } catch (err) {
      results.failed++;
      results.errors.push(`Row ${i + 2} (${vendorName}): ${err.message}`);
    }
  }
  return results;
};

// ─── Excel reader ───────────────────────────────────────────────────────────
export const readExcelFile = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf);
  // Pick first sheet that isn't named "Instructions"
  const sheetName = wb.SheetNames.find(n => n.toLowerCase() !== 'instructions') || wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet);
  return rows;
};
